import { SaveDialogContent, SaveButton } from "@/components/SaveForm";
import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PlanUsage } from "@/components/PlanUsage";
import { Users as UsersIcon, Plus, UserCog, Pencil, KeyRound, Trash2, AlertTriangle } from "lucide-react";

const inp = "bg-slate-950 border-slate-700 text-slate-100 mt-1";

export default function Users() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "ADMINISTRADOR";
  const [list, setList] = useState(null);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ role: "CONSULTA", password: "" });
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [resetUser, setResetUser] = useState(null);
  const [resetForm, setResetForm] = useState({ new_password: "", confirm_password: "" });
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [myPasswordOpen, setMyPasswordOpen] = useState(false);

  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/roles").then((r) => setRoles(r.data)); }, []);

  const setCreateOpen = (value) => {
    setOpen(value);
    if (!value) setForm({ role: "CONSULTA", password: "" });
  };

  const save = async () => {
    if (!form.email || !form.name) { toast.error("Nome e e-mail são obrigatórios."); return; }
    if (!form.password.trim() || Array.from(form.password).length < 6) { toast.error("Informe uma senha com ao menos 6 caracteres."); return; }
    if (new TextEncoder().encode(form.password).length > 72) { toast.error("A senha deve ter no máximo 72 bytes em UTF-8."); return; }
    try { await api.post("/users", form); toast.success("Usuário criado."); setOpen(false); setForm({ role: "CONSULTA", password: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (u) => { try { await api.put(`/users/${u.id}`, { active: !u.active }); toast.success(!u.active ? "Usuário ativado." : "Usuário inativado."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const changeRole = async (u, role) => { try { await api.put(`/users/${u.id}`, { role }); toast.success("Perfil atualizado."); load(); } catch (e) { toast.error(apiErr(e)); } };

  const openEdit = (u) => { setEditUser(u); setEditForm({ name: u.name, email: u.email, role: u.role, active: u.active }); };
  const saveEdit = async () => {
    try { await api.put(`/users/${editUser.id}`, editForm); toast.success("Usuário atualizado."); setEditUser(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const submitReset = async () => {
    if (resetForm.new_password.length < 6) { toast.error("A nova senha deve ter ao menos 6 caracteres."); return; }
    if (resetForm.new_password !== resetForm.confirm_password) { toast.error("As senhas não conferem."); return; }
    try { await api.post(`/users/${resetUser.id}/reset-password`, resetForm); toast.success("Senha redefinida com sucesso."); setResetUser(null); setResetForm({ new_password: "", confirm_password: "" }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openDelete = async (u) => {
    try {
      const { data } = await api.get(`/users/${u.id}/has-history`);
      setDeleteUser({ ...u, has_history: data.has_history });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const confirmDelete = async () => {
    setDeleteBusy(true);
    try { await api.delete(`/users/${deleteUser.id}/permanent`, { params: deleteUser.has_history ? { force: true } : {} }); toast.success("Usuário excluído definitivamente."); setDeleteUser(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setDeleteBusy(false); }
  };

  if (list === null) return <Skeleton className="h-96 bg-slate-800" />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold text-slate-50">Usuários</h1><p className="text-slate-400 text-sm">Perfis e permissões (validadas no backend).</p></div>
        <PlanUsage kind="usuarios" refreshKey={list} />
        <Button data-testid="btn-new-user" onClick={() => setOpen(true)} className="bg-sky-700 hover:bg-sky-600"><Plus className="h-4 w-4 mr-1" /> Novo usuário</Button>
      </div>
      <Card className="card-metal p-0 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
            <th className="text-left px-4 py-3">Nome</th><th className="text-left px-4 py-3">E-mail</th><th className="text-left px-4 py-3">Perfil</th><th className="text-center px-4 py-3">Ativo</th><th className="text-right px-4 py-3">Ações</th>
          </tr></thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-800">
                <td className="px-4 py-2.5 text-slate-100 flex items-center gap-2">
                  <UserCog className="h-4 w-4 text-sky-400" />
                  {u.name}
                  {u.is_platform_admin && <span className="text-[10px] uppercase font-mono text-violet-300 border border-violet-800/60 rounded px-1.5 py-0.5">Superadmin</span>}
                </td>
                <td className="px-4 py-2.5 text-slate-400">{u.email}</td>
                <td className="px-4 py-2.5">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.is_platform_admin}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 h-8 w-44"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-2.5 text-center">
                  <Switch checked={u.active} disabled={u.is_platform_admin} onCheckedChange={() => toggle(u)} data-testid={`user-active-${u.id}`} />
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {u.is_platform_admin ? (
                    u.id === me?.id ? (
                      <Button size="sm" variant="ghost" className="text-amber-300 h-8" onClick={() => setMyPasswordOpen(true)} data-testid="btn-change-own-superadmin-password">
                        <KeyRound className="h-3.5 w-3.5 mr-1" /> Alterar minha senha
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-500 font-mono">Conta protegida</span>
                    )
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" className="text-slate-300 h-8" onClick={() => openEdit(u)} data-testid={`btn-edit-user-${u.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Editar</Button>
                      <Button size="sm" variant="ghost" className="text-amber-300 h-8" onClick={() => setResetUser(u)} data-testid={`btn-reset-user-${u.id}`}><KeyRound className="h-3.5 w-3.5 mr-1" />Redefinir senha</Button>
                      {isAdmin && u.id !== me?.id && (
                        <Button size="sm" variant="ghost" className="text-rose-400 h-8" onClick={() => openDelete(u)} data-testid={`btn-delete-user-${u.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Excluir</Button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>
      <Dialog open={open} onOpenChange={setCreateOpen}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader><DialogTitle className="font-display text-2xl">Novo usuário</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input className={inp} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>E-mail *</Label><Input type="email" className={inp} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Senha *</Label><Input data-testid="input-new-user-password" type="password" autoComplete="new-password" required minLength={6} className={inp} placeholder="Ao menos 6 caracteres" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div><Label>Perfil</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button><SaveButton data-testid="btn-save-user" onClick={save} className="bg-sky-700 hover:bg-sky-600">Salvar</SaveButton></DialogFooter>
        </SaveDialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader><DialogTitle className="font-display text-2xl">Editar usuário</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input data-testid="input-edit-name" className={inp} value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div><Label>E-mail / Login</Label><Input data-testid="input-edit-email" type="email" className={inp} value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div><Label>Perfil</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-950/60 px-3 py-2"><Label>Status: Ativo</Label><Switch data-testid="switch-edit-active" checked={editForm.active} onCheckedChange={(v) => setEditForm({ ...editForm, active: v })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setEditUser(null)}>Cancelar</Button><SaveButton data-testid="btn-save-edit-user" onClick={saveEdit} className="bg-sky-700 hover:bg-sky-600">Salvar alterações</SaveButton></DialogFooter>
        </SaveDialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader><DialogTitle className="font-display text-2xl">Redefinir senha</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">Definir nova senha para <b className="text-slate-200">{resetUser?.name}</b>. A senha atual nunca é exibida.</p>
          <div className="space-y-3">
            <div><Label>Nova senha</Label><Input data-testid="input-reset-new" type="password" className={inp} value={resetForm.new_password} onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })} /></div>
            <div><Label>Confirmar nova senha</Label><Input data-testid="input-reset-confirm" type="password" className={inp} value={resetForm.confirm_password} onChange={(e) => setResetForm({ ...resetForm, confirm_password: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setResetUser(null)}>Cancelar</Button><SaveButton data-testid="btn-submit-reset" onClick={submitReset} className="bg-amber-600 hover:bg-amber-500">Redefinir senha</SaveButton></DialogFooter>
        </SaveDialogContent>
      </Dialog>
      <ChangePasswordDialog open={myPasswordOpen} onOpenChange={setMyPasswordOpen} />

      <Dialog open={!!deleteUser} onOpenChange={(v) => !v && setDeleteUser(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="dialog-delete-user">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Trash2 className="h-6 w-6 text-rose-400" /> Excluir definitivamente</DialogTitle></DialogHeader>
          {deleteUser?.has_history ? (
            <div className="rounded-lg border border-rose-800/60 bg-rose-950/30 p-4 space-y-2">
              <div className="flex items-center gap-2 text-rose-300 font-semibold"><AlertTriangle className="h-5 w-5" /> Este usuário possui histórico relacionado</div>
              <p className="text-sm text-rose-100" data-testid="delete-user-history-msg">Durante esta fase de implantação, é permitido excluir também os dados dependentes de demonstração (movimentações, inventários, aprovações, alertas tratados e auditoria).</p>
              <p className="text-sm text-rose-200 font-medium">Esta operação não poderá ser desfeita. Deseja continuar?</p>
            </div>
          ) : (
            <p className="text-sm text-slate-300" data-testid="delete-user-confirm-msg">Excluir definitivamente <b className="text-slate-100">{deleteUser?.name}</b>? Esta operação não poderá ser desfeita.</p>
          )}
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setDeleteUser(null)} className="w-full sm:w-auto">Cancelar</Button>
            <Button data-testid="btn-confirm-delete-user" onClick={confirmDelete} disabled={deleteBusy} className="w-full sm:w-auto bg-rose-700 hover:bg-rose-600">{deleteBusy ? "Excluindo..." : "Excluir definitivamente"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
